import { gql } from '@apollo/client';
import {
  Box,
  Button,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useState } from 'react';
import { ChevronRightIcon, SearchIcon } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { PaginationServerSide } from '~components/ui/PaginationServerSide';
import { ConfigureAdminRolesModal_UserFragment } from './__generated__/ConfigureAdminRolesModal.graphql';
import { useSearchUserModalQuery } from './__generated__/SearchUserModal.graphql';

const PAGE_SIZE = 5;

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSelectUser: (user: ConfigureAdminRolesModal_UserFragment) => void;
}

export function SearchUserModal({ isOpen, onCancel, onSelectUser }: Props) {
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);

  const { data, loading } = useSearchUserModalQuery({
    variables: { search, pageSize: PAGE_SIZE, pageNumber: pageIndex + 1 },
    skip: !search,
  });

  const users = data?.serviceAdminEligibleUsers.users ?? [];
  const totalRowCount = data?.serviceAdminEligibleUsers.totalCount ?? 0;
  const pageCount = Math.ceil(totalRowCount / PAGE_SIZE);

  const handleClose = () => {
    setSearchText('');
    setSearch('');
    setPageIndex(0);
    onCancel();
  };

  const handleSearchClick = () => {
    setPageIndex(0);
    setSearch(searchText.trim());
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('addAdmin')}</ModalHeader>
        <ModalCloseButton onClick={handleClose} />
        <ModalBody paddingBottom="6">
          <FormLabel mb="2">{t('searchForUser')}</FormLabel>
          <InputGroup marginBottom="4">
            <InputLeftElement pointerEvents="none" height="full">
              <SearchIcon color="gray.500" />
            </InputLeftElement>
            <Input
              autoFocus
              placeholder={t('searchForUserPlaceholder')}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSearchClick();
                }
              }}
            />
          </InputGroup>

          {search && !loading && users.length === 0 && (
            <Text color="gray.500" textAlign="center" paddingY="6">
              {t('noEligibleUsersFound')}
            </Text>
          )}

          {users.length > 0 && (
            <Box
              boxShadow="elevated"
              border="1px solid"
              borderRadius="md"
              borderColor="gray.100"
              overflowX="auto"
              bgColor="white"
            >
              <Table variant="simple" width="100%">
                <Thead>
                  <Tr>
                    <Th>{t('name')}</Th>
                    <Th>{t('email')}</Th>
                    <Th>{t('role')}</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {users.map((user) => (
                    <Tr key={user.id}>
                      <Td>
                        {user.givenName} {user.familyName}
                      </Td>
                      <Td>{user.email}</Td>
                      <Td>{user.roles.join(', ')}</Td>
                      <Td textAlign="right">
                        <IconButton
                          aria-label={t('addAdmin')}
                          icon={<ChevronRightIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() => onSelectUser(user)}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {totalRowCount > PAGE_SIZE && (
            <Box display="flex" justifyContent="flex-end" marginTop="4">
              <PaginationServerSide
                pageCount={pageCount}
                currentPageIndex={pageIndex}
                hasPreviousPage={pageIndex > 0}
                hasNextPage={pageIndex < pageCount - 1}
                goToPage={setPageIndex}
                goToPreviousPage={() => setPageIndex((prev) => prev - 1)}
                goToNextPage={() => setPageIndex((prev) => prev + 1)}
              />
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" colorScheme="gray" marginLeft="auto" onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button variant="solid" colorScheme="blue" marginLeft="3" onClick={handleSearchClick}>
            {t('search')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

gql`
  query SearchUserModal($search: String, $pageSize: Int, $pageNumber: Int) {
    serviceAdminEligibleUsers(search: $search, pageSize: $pageSize, pageNumber: $pageNumber) {
      totalCount
      users {
        ...ConfigureAdminRolesModal_user
      }
    }
  }
`;
